import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * <ct-complete-set> - "Complete The Set" add-ons on the PDP
 * (blocks/ct-complete-set.liquid).
 *
 * When this section's product form successfully adds the canvas, any checked
 * add-ons are added to the cart in one /cart/add.js call, then a cart update
 * event is fired so the cart drawer, cart count and cart page refresh.
 * Also swaps in fresh markup on variant change (mirrors pdp-badges.js).
 * @extends {Component}
 */
class CtCompleteSet extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  /** @returns {{id: number, quantity: number}[]} */
  #selectedItems() {
    /** @type {{id: number, quantity: number}[]} */
    const items = [];
    this.querySelectorAll('.ct-set__check:checked').forEach((box) => {
      try {
        const parsed = JSON.parse(/** @type {HTMLElement} */ (box).dataset.addonItems || '[]');
        for (const item of parsed) {
          if (item && item.id && item.quantity > 0) items.push({ id: Number(item.id), quantity: Number(item.quantity) });
        }
      } catch (e) {
        /* ignore malformed row */
      }
    });
    return items;
  }

  #onCartUpdate = (event) => {
    if (event.target === this || event.action !== 'add') return;
    const form = this.#section?.querySelector('product-form-component');
    if (!form || event.target !== form) return;

    const items = this.#selectedItems();
    if (!items.length) return;

    event.promise
      ?.then((result) => {
        if (result?.detail?.didError) return;
        return this.#addExtras(items);
      })
      .catch(() => {});
  };

  /** @param {{id: number, quantity: number}[]} items */
  async #addExtras(items) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const status = this.querySelector('[data-set-status]');
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const didError = Boolean(data.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: didError ? undefined : data.sections,
          didError,
          source: 'ct-complete-set',
        },
      });
      if (status) status.textContent = didError ? data.description || data.message || '' : 'Add-ons added to your cart.';
    } catch (error) {
      console.warn('[ct-complete-set] Could not add add-ons:', error);
      deferred.resolve({ cart: null, detail: { didError: true, source: 'ct-complete-set' } });
    }
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const fresh = detail.html.querySelector(`ct-complete-set[data-block-id="${this.dataset.blockId}"]`);
        if (!fresh) return;
        const checked = new Set(
          Array.from(this.querySelectorAll('.ct-set__check:checked')).map((el) => el.id)
        );
        const unchecked = new Set(
          Array.from(this.querySelectorAll('.ct-set__check:not(:checked)')).map((el) => el.id)
        );
        this.innerHTML = fresh.innerHTML;
        this.querySelectorAll('.ct-set__check').forEach((el) => {
          const box = /** @type {HTMLInputElement} */ (el);
          if (checked.has(box.id)) box.checked = true;
          else if (unchecked.has(box.id)) box.checked = false;
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[ct-complete-set] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('ct-complete-set')) {
  customElements.define('ct-complete-set', CtCompleteSet);
}
